import { defineStore } from "pinia"
import { ref } from "vue"
import { getReservationPendingSummary } from "@/api/reservation"

export const useReservationPendingStore = defineStore("reservationPending", () => {
  const activeShopId = ref<number>()
  const pendingCount = ref(0)
  const earliestPendingDate = ref<string | null>(null)
  const asOf = ref<string>()
  const loading = ref(false)
  const error = ref("")
  const authorized = ref(false)
  let contextSequence = 0
  let requestSequence = 0

  function clearSummary() {
    pendingCount.value = 0
    earliestPendingDate.value = null
    asOf.value = undefined
    loading.value = false
    error.value = ""
  }

  function clear() {
    contextSequence += 1
    requestSequence += 1
    authorized.value = false
    activeShopId.value = undefined
    clearSummary()
  }

  async function refresh() {
    const shopId = activeShopId.value
    if (!authorized.value || !shopId) return
    const currentContext = contextSequence
    const currentRequest = ++requestSequence
    loading.value = true
    error.value = ""
    try {
      const summary = await getReservationPendingSummary(shopId)
      if (currentContext !== contextSequence || currentRequest !== requestSequence) return
      pendingCount.value = Math.max(0, Math.trunc(summary.pendingCount))
      earliestPendingDate.value = summary.earliestPendingDate ?? null
      asOf.value = summary.asOf
    }
    catch (caught) {
      if (currentContext !== contextSequence || currentRequest !== requestSequence) return
      const failure = caught as { code?: number, status?: number }
      if (failure.code === 47601 || failure.code === 47610 || failure.status === 403) {
        clear()
        return
      }
      error.value = "待确认预订数量加载失败"
    }
    finally {
      if (currentContext === contextSequence && currentRequest === requestSequence) {
        loading.value = false
      }
    }
  }

  async function activate(shopId: number | undefined, canView: boolean) {
    if (!canView || !isPositiveShopId(shopId)) {
      clear()
      return
    }

    const changed = !authorized.value || activeShopId.value !== shopId
    authorized.value = true
    if (changed) {
      contextSequence += 1
      requestSequence += 1
      activeShopId.value = shopId
      clearSummary()
    }
    await refresh()
  }

  return {
    activeShopId,
    asOf,
    authorized,
    clear,
    earliestPendingDate,
    error,
    loading,
    pendingCount,
    activate,
    refresh,
  }
})

function isPositiveShopId(value: number | undefined): value is number {
  return Number.isInteger(value) && Number(value) > 0
}
